import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Globe2, Check, X, MapPin } from "lucide-react";
import api from "../../lib/api";

/* HomeZoneCard — quick "Set home time zone" card on the Travel Hub.
   Auto-hides once the traveller has a home_timezone on file. Otherwise
   detects their browser tz via Intl.DateTimeFormat and offers a one-tap
   save. This ensures every trip email/notif gets a personalised · IST
   / · JST / · PDT badge without ever going into Settings.

   Backend endpoints:
     GET  /api/travel/prefs
     PUT  /api/travel/prefs   body: { home_timezone: "Asia/Kolkata" }
*/

// A short curated list; the browser-detected zone appears at the top too.
const COMMON_ZONES = [
  "Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Asia/Tokyo", "Asia/Shanghai",
  "Europe/London", "Europe/Paris", "Europe/Berlin", "Europe/Istanbul",
  "America/New_York", "America/Chicago", "America/Los_Angeles", "America/Toronto",
  "America/Sao_Paulo", "Africa/Cairo", "Africa/Johannesburg",
  "Australia/Sydney", "Pacific/Auckland",
];

function detectTz() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch { return ""; }
}

function tzAbbrev(tz) {
  try {
    const fmt = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" });
    const part = fmt.formatToParts(new Date()).find((p) => p.type === "timeZoneName");
    return part?.value || tz.split("/").pop().replace(/_/g, " ");
  } catch { return tz.split("/").pop().replace(/_/g, " "); }
}

export default function HomeZoneCard() {
  const [state, setState] = useState({ loaded: false, current: null, dismissed: false });
  const [saving, setSaving] = useState(false);
  const [choice, setChoice] = useState("");

  const detected = detectTz();

  useEffect(() => {
    let live = true;
    api.get("/travel/prefs").then((r) => {
      if (!live) return;
      setState({ loaded: true, current: r.data?.home_timezone || null, dismissed: false });
      setChoice(r.data?.home_timezone || detected || "");
    }).catch(() => setState({ loaded: true, current: null, dismissed: false }));
    return () => { live = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!state.loaded || state.dismissed) return null;

  // Compact "already set" strip — the badge quietly shows the current zone
  // and offers a Change action so travellers can jump if they've relocated.
  if (state.current) {
    return (
      <div className="mx-auto max-w-3xl px-5 mb-2" data-testid="home-zone-card-compact">
        <div className="flex items-center justify-between p-2.5 border border-emerald-200 bg-emerald-50/60 rounded text-xs">
          <div className="inline-flex items-center gap-1.5 text-emerald-900">
            <Globe2 className="w-3.5 h-3.5" />
            Trip emails are stamped in <b>{tzAbbrev(state.current)}</b> ({state.current})
          </div>
          <button onClick={() => setState((s) => ({ ...s, current: null }))}
            data-testid="home-zone-change"
            className="text-emerald-700 hover:underline font-700">Change</button>
        </div>
      </div>
    );
  }

  // Ordered options: detected first, then curated list (deduped).
  const opts = Array.from(new Set([detected, ...COMMON_ZONES].filter(Boolean)));

  const save = async () => {
    if (!choice) return toast.error("Pick a zone");
    setSaving(true);
    try {
      await api.put("/travel/prefs", { home_timezone: choice });
      setState({ loaded: true, current: choice, dismissed: false });
      toast.success(`Home zone set to ${tzAbbrev(choice)}`);
    } catch { toast.error("Could not save zone — try again"); }
    setSaving(false);
  };

  return (
    <div className="mx-auto max-w-3xl px-5 mb-4" data-testid="home-zone-card">
      <div className="p-4 border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-white rounded-2xl">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-emerald-600 grid place-items-center shrink-0">
            <Globe2 className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-serif font-800 text-lg text-slate-900 leading-tight">Set your home time zone</h3>
            <p className="text-xs text-slate-600 mt-1 leading-snug">
              We'll stamp every trip email &amp; reminder with your zone (e.g. <b>Monday, Sept 21 · IST</b>) so dates are never ambiguous — no more "wait, is that in my time or the destination's?"
            </p>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              {detected && (
                <button onClick={() => setChoice(detected)} data-testid="home-zone-detected"
                  className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-700 rounded-full border-2 transition
                    ${choice === detected ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-emerald-800 border-emerald-300 hover:border-emerald-500"}`}>
                  <MapPin className="w-3.5 h-3.5" /> Use my current zone · {tzAbbrev(detected)}
                </button>
              )}
              <select value={choice} onChange={(e) => setChoice(e.target.value)}
                data-testid="home-zone-select"
                className="text-xs font-mono border-2 border-slate-300 focus:border-emerald-500 rounded px-2 py-1.5 bg-white">
                {opts.map((tz) => <option key={tz} value={tz}>{tz} ({tzAbbrev(tz)})</option>)}
              </select>
            </div>

            <div className="mt-3 flex items-center gap-2">
              <button onClick={save} disabled={saving || !choice} data-testid="home-zone-save"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-sm font-700 rounded-full">
                <Check className="w-4 h-4" /> {saving ? "Saving…" : "Save home zone"}
              </button>
              <button onClick={() => setState((s) => ({ ...s, dismissed: true }))}
                data-testid="home-zone-dismiss"
                className="text-xs text-slate-500 hover:text-slate-700 font-700">Not now</button>
            </div>
          </div>
          <button onClick={() => setState((s) => ({ ...s, dismissed: true }))}
            aria-label="Dismiss" className="text-slate-400 hover:text-slate-700 shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
